// frontend/src/hooks/useEntropyToken.js
import { useState, useEffect, useCallback } from "react";
import web3Service from "../services/web3Service";
import { ethers } from "ethers";

/**
 * Hook React pour interagir avec le contrat EntropyToken (ENTQ)
 */
export function useEntropyToken() {
  const [contract, setContract] = useState(null);
  const [address, setAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialise le contrat
  useEffect(() => {
    initContract();
  }, []);

  const initContract = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const entropyToken = await web3Service.getContract("EntropyToken");
      setContract(entropyToken);
      
      const addr = await entropyToken.getAddress();
      setAddress(addr);

      console.log("✅ EntropyToken initialisé à", addr);
    } catch (err) {
      console.error("❌ Erreur d'initialisation EntropyToken:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Récupère le nom du token
  const getName = useCallback(async () => {
    if (!contract) return "";

    try {
      return await contract.name();
    } catch (err) {
      console.error("❌ Erreur getName:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le symbole du token
  const getSymbol = useCallback(async () => {
    if (!contract) return "";

    try {
      return await contract.symbol();
    } catch (err) {
      console.error("❌ Erreur getSymbol:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le total supply
  const getTotalSupply = useCallback(async () => {
    if (!contract) return "0";

    try {
      const supply = await contract.totalSupply();
      return ethers.formatEther(supply);
    } catch (err) {
      console.error("❌ Erreur getTotalSupply:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le solde d'une adresse
  const getBalance = useCallback(async (userAddress) => {
    if (!contract) return "0";

    try {
      const balance = await contract.balanceOf(userAddress);
      return ethers.formatEther(balance);
    } catch (err) {
      console.error("❌ Erreur getBalance:", err);
      throw err;
    }
  }, [contract]);

  // Transfère des tokens
  const transfer = useCallback(async (toAddress, amount) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const amountWei = ethers.parseEther(amount.toString());
      const tx = await contract.transfer(toAddress, amountWei);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur transfer:", err);
      throw err;
    }
  }, [contract]);

  // Approuve une dépense
  const approve = useCallback(async (spenderAddress, amount) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const amountWei = ethers.parseEther(amount.toString());
      const tx = await contract.approve(spenderAddress, amountWei);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur approve:", err);
      throw err;
    }
  }, [contract]);

  // Récupère l'allocation (allowance)
  const getAllowance = useCallback(async (ownerAddress, spenderAddress) => {
    if (!contract) return "0";

    try {
      const allowance = await contract.allowance(ownerAddress, spenderAddress);
      return ethers.formatEther(allowance);
    } catch (err) {
      console.error("❌ Erreur getAllowance:", err);
      throw err;
    }
  }, [contract]);

  // Mint des tokens (si autorisé)
  const mint = useCallback(async (toAddress, amount) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const amountWei = ethers.parseEther(amount.toString());
      const tx = await contract.mint(toAddress, amountWei);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur mint:", err);
      throw err;
    }
  }, [contract]);

  // Écoute les événements Transfer
  const onTransfer = useCallback((callback) => {
    if (!contract) return;

    const filter = contract.filters.Transfer();
    
    contract.on(filter, (from, to, value, event) => {
      callback({
        from,
        to,
        value: ethers.formatEther(value),
        transactionHash: event?.log?.transactionHash || event?.transactionHash || "",
        blockNumber: event?.log?.blockNumber || event?.blockNumber || 0
      });
    });

    // Retourne une fonction de nettoyage
    return () => {
      contract.off(filter);
    };
  }, [contract]);

  return {
    contract,
    address,
    isLoading,
    error,
    getName,
    getSymbol,
    getTotalSupply,
    getBalance,
    transfer,
    approve,
    getAllowance,
    mint,
    onTransfer,
  };
}
